import React, { useEffect, useState } from 'react';
import './CardsList.css';
import { useAuth } from '@/hooks/useAuth';
import { Loading, BusinessCardItem, QRModal, BusinessCardScanner } from '@/components';
import { useCardsStore, useProfileStore } from '@/stores';
import { createBrandedCardImage, generateCardFileName } from '@/utils/cardImage.utils';
import { downloadVCard } from '@/utils/vcard.utils';
import { mergeScanWithAzureAD } from '@/utils/scanToCard.utils';
import { 
  QrCode, Plus, ArrowLeft, 
  AlertCircle, RefreshCw, Camera
} from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';

const CardsList: React.FC = () => {
  const { getAccessToken } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Zustand stores
  const cards = useCardsStore(state => state.cards);
  const loading = useCardsStore(state => state.loading);
  const error = useCardsStore(state => state.error);
  const fetchCards = useCardsStore(state => state.fetchCards);
  const deleteCard = useCardsStore(state => state.deleteCard);
  const setDefaultCardInStore = useCardsStore(state => state.setDefaultCard);
  const maxCardsLimit = useCardsStore(state => state.maxCardsLimit);
  const canCreateCard = useCardsStore(state => state.canCreateCard);
  
  const azureProfile = useProfileStore(state => state.azureProfile);
  const fetchProfile = useProfileStore(state => state.fetchProfile);
  
  // Local UI state
  const [showQrModal, setShowQrModal] = useState(false);
  const [selectedCard, setSelectedCard] = useState<any>(null);
  const [showScanner, setShowScanner] = useState(false);

  useEffect(() => {
    loadData();
    if ((location.state as any)?.openScanner) {
      setShowScanner(true);
      window.history.replaceState({}, '');
    }
  }, []);

  const loadData = async () => {
    try {
      const token = await getAccessToken(true);
      
      // Stores handle everything (caching, loading states, error handling)
      await Promise.all([
        fetchCards(token),
        fetchProfile(token)
      ]);
    } catch (err) {
      console.error('Failed to load data:', err);
    }
  };

  const handleCreateCard = () => {
    // Check limit before navigation
    if (!canCreateCard()) {
      alert(`You have reached the maximum limit of ${maxCardsLimit} cards. Please delete an existing card to create a new one.`);
      return;
    }
    // No need to pass state - components use stores
    navigate('/cards/new', { state: { isNewCard: true, fromRoute: '/cards' } });
  };

  const handleScanMyCard = () => {
    if (!canCreateCard()) {
      alert(`You have reached the maximum limit of ${maxCardsLimit} cards. Please delete an existing card to create a new one.`);
      return;
    }
    setShowScanner(true);
  };

  const handleScanSuccess = (scanResult: any) => {
    const { cardData, cardName } = mergeScanWithAzureAD(scanResult.data, azureProfile);
    navigate('/cards/new', {
      state: { isNewCard: true, fromRoute: '/cards', scannedCardData: cardData, scannedCardName: cardName }
    });
  };

  const handleQrClick = (card: any) => {
    setSelectedCard(card);
    setShowQrModal(true);
  };

  const handleEditCard = (card: any) => {
    navigate(`/cards/edit/${card.cardId}`, { state: { isEdit: true, fromRoute: '/cards' } });
  };

  const handleViewCard = (card: any) => {
    navigate(`/cards/view/${card.cardId}`, { state: { isViewMode: true, fromRoute: '/cards' } });
  };

  const handleSetDefaultCard = async (card: any) => {
    try {
      const token = await getAccessToken(true);
      await setDefaultCardInStore(card.cardId, token);
    } catch (err) {
      console.error('Failed to set default card:', err);
    }
  };

  const handleDeleteCard = async (card: any) => {
    const confirmed = window.confirm(
      `Are you sure you want to delete "${card.cardName}"? This action cannot be undone.`
    );
    
    if (!confirmed) return;
    
    try {
      const token = await getAccessToken(true);
      await deleteCard(card.cardId, token);
    } catch (err) {
      console.error('Failed to delete card:', err);
    }
  };

  const handleDownloadQR = async () => {
    if (!selectedCard?.qrCodeUrl) return;
    
    try {
      // Extract card data for branded image
      const primaryEmail = selectedCard.emails.find((e: any) => e.is_primary)?.email || selectedCard.emails[0]?.email || '';
      const primaryPhone = selectedCard.phones.find((p: any) => p.is_primary)?.number || selectedCard.phones[0]?.number || '';
      
      const formData = {
        displayName: selectedCard.displayName || '',
        givenName: selectedCard.givenName || '',
        surname: selectedCard.surname || '',
        jobTitle: selectedCard.jobTitle || '',
        companyName: selectedCard.companyName || '',
        department: selectedCard.department || '',
        mail: primaryEmail,
        mobilePhone: primaryPhone,
        businessPhones: [],
        additionalEmails: selectedCard.emails.filter((e: any) => !e.is_primary).map((e: any) => e.email),
        additionalPhones: selectedCard.phones.filter((p: any) => !p.is_primary).map((p: any) => ({ type: p.type, number: p.number })),
        address: selectedCard.addresses[0],
        additionalAddresses: selectedCard.addresses.slice(1),
        website: selectedCard.website || '',
        socialProfiles: selectedCard.socialProfiles || [],
        notes: selectedCard.notes || '',
      };
      
      const brandedImage = await createBrandedCardImage({
        qrCodeUrl: selectedCard.qrCodeUrl,
        formData,
        primaryColor: '#1E3A8A',
        secondaryColor: '#FFFFFF',
        azureProfileId: azureProfile?.id,
        getAccessToken: () => getAccessToken(false)
      }, 'png');
      
      const link = document.createElement('a');
      link.href = brandedImage;
      link.download = generateCardFileName(selectedCard.displayName, 'png');
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Failed to download QR:', error);
    }
  };

  const handleDownloadVCard = () => {
    if (!selectedCard) return;
    
    const primaryEmail = selectedCard.emails.find((e: any) => e.is_primary)?.email || selectedCard.emails[0]?.email || '';
    const primaryPhone = selectedCard.phones.find((p: any) => p.is_primary)?.number || selectedCard.phones[0]?.number || '';
    
    // Create BusinessCardData format for vCard utility
    const vCardData: any = {
      displayName: selectedCard.displayName || '',
      givenName: selectedCard.givenName || '',
      surname: selectedCard.surname || '',
      jobTitle: selectedCard.jobTitle || '',
      companyName: selectedCard.companyName || '',
      department: selectedCard.department || '',
      mail: primaryEmail,
      mobilePhone: primaryPhone,
      businessPhones: [],
      additionalEmails: selectedCard.emails.filter((e: any) => !e.is_primary).map((e: any) => e.email),
      additionalPhones: selectedCard.phones.filter((p: any) => !p.is_primary).map((p: any) => ({ type: p.type, number: p.number })),
      address: selectedCard.addresses[0],
      additionalAddresses: selectedCard.addresses.slice(1),
      website: selectedCard.website || '',
      socialProfiles: selectedCard.socialProfiles || [],
      notes: selectedCard.notes || '',
    };
    
    downloadVCard(vCardData);
  };

  const handleShare = async () => {
    if (!selectedCard?.qrCodeUrl) return;
    
    if (navigator.share) {
      try {
        // Extract card data for branded image
        const primaryEmail = selectedCard.emails.find((e: any) => e.is_primary)?.email || selectedCard.emails[0]?.email || '';
        const primaryPhone = selectedCard.phones.find((p: any) => p.is_primary)?.number || selectedCard.phones[0]?.number || '';
        
        const formData = {
          displayName: selectedCard.displayName || '',
          givenName: selectedCard.givenName || '',
          surname: selectedCard.surname || '',
          jobTitle: selectedCard.jobTitle || '',
          companyName: selectedCard.companyName || '',
          department: selectedCard.department || '',
          mail: primaryEmail,
          mobilePhone: primaryPhone,
          businessPhones: [],
          additionalEmails: selectedCard.emails.filter((e: any) => !e.is_primary).map((e: any) => e.email),
          additionalPhones: selectedCard.phones.filter((p: any) => !p.is_primary).map((p: any) => ({ type: p.type, number: p.number })),
          address: selectedCard.addresses[0],
          additionalAddresses: selectedCard.addresses.slice(1),
          website: selectedCard.website || '',
          socialProfiles: selectedCard.socialProfiles || [],
          notes: selectedCard.notes || '',
        };
        
        const brandedImage = await createBrandedCardImage({
          qrCodeUrl: selectedCard.qrCodeUrl,
          formData,
          primaryColor: '#1E3A8A',
          secondaryColor: '#FFFFFF',
          azureProfileId: azureProfile?.id,
          getAccessToken: () => getAccessToken(false)
        }, 'png');
        
        const response = await fetch(brandedImage);
        const blob = await response.blob();
        const file = new File([blob], generateCardFileName(selectedCard.displayName, 'png'), { type: 'image/png' });
        
        await navigator.share({
          title: `${selectedCard.cardName} - Business Card`,
          text: `Check out ${selectedCard.cardName}'s digital business card`,
          files: [file]
        });
      } catch (error) {
        console.error('Error sharing:', error);
      }
    }
  };

  const handleEmailQR = async () => {
    if (!selectedCard || !selectedCard.qrCodeUrl) return;
    
    try {
      // Extract card data for branded image
      const primaryEmail = selectedCard.emails.find((e: any) => e.is_primary)?.email || selectedCard.emails[0]?.email || '';
      const primaryPhone = selectedCard.phones.find((p: any) => p.is_primary)?.number || selectedCard.phones[0]?.number || '';
      
      const formData = {
        displayName: selectedCard.displayName || '',
        givenName: selectedCard.givenName || '',
        surname: selectedCard.surname || '',
        jobTitle: selectedCard.jobTitle || '',
        companyName: selectedCard.companyName || '',
        department: selectedCard.department || '',
        mail: primaryEmail,
        mobilePhone: primaryPhone,
        businessPhones: [],
        additionalEmails: selectedCard.emails.filter((e: any) => !e.is_primary).map((e: any) => e.email),
        additionalPhones: selectedCard.phones.filter((p: any) => !p.is_primary).map((p: any) => ({ type: p.type, number: p.number })),
        address: selectedCard.addresses[0],
        additionalAddresses: selectedCard.addresses.slice(1),
        website: selectedCard.website || '',
        socialProfiles: selectedCard.socialProfiles || [],
        notes: selectedCard.notes || '',
      };
      
      const brandedImage = await createBrandedCardImage({
        qrCodeUrl: selectedCard.qrCodeUrl,
        formData,
        primaryColor: '#1E3A8A',
        secondaryColor: '#FFFFFF',
        azureProfileId: azureProfile?.id,
        getAccessToken: () => getAccessToken(false)
      }, 'png');
      
      const fileName = generateCardFileName(selectedCard.displayName, 'png');
      const blob = await fetch(brandedImage).then(r => r.blob());
      const file = new File([blob], fileName, { type: 'image/png' });

      // Use Web Share API with file attachment
      if (navigator.share && navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({
          title: `${selectedCard.cardName} - Digital Business Card`,
          text: `${selectedCard.displayName}${selectedCard.jobTitle ? ' | ' + selectedCard.jobTitle : ''}`,
          files: [file],
        });
      } else {
        // Fallback: download + mailto
        const link = document.createElement('a');
        link.href = brandedImage;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        setTimeout(() => {
          const subject = encodeURIComponent(`${selectedCard.cardName} - Digital Business Card`);
          const body = encodeURIComponent(`Hi,\n\nPlease find my digital business card attached (just downloaded to your device).\n\nBest regards,\n${selectedCard.displayName}\n${selectedCard.jobTitle || ''}\n${selectedCard.companyName || ''}`);
          window.location.href = `mailto:?subject=${subject}&body=${body}`;
        }, 500);
      }
    } catch (error) {
      console.error('Error preparing email:', error);
    }
  };

  if (loading) {
    return <Loading message="Loading your cards..." />;
  }

  return (
    <div className="cards-list-container">
      <div className="cards-list-header">
        <button onClick={() => navigate('/')} className="btn-back">
          <ArrowLeft size={20} />
          <span>Back to Dashboard</span>
        </button>
        <div className="header-content">
          <h1>
            <QrCode size={32} />
            Your Business Cards
          </h1>
          <p className="header-description">
            Manage all your digital business cards in one place
            <span style={{ marginLeft: '10px', color: cards.length >= maxCardsLimit ? '#DC2626' : cards.length >= maxCardsLimit - 2 ? '#F59E0B' : '#10B981', fontWeight: 600 }}>
              ({cards.length}/{maxCardsLimit} cards)
            </span>
          </p>
        </div>
        <div className="header-actions">
          <button 
            onClick={handleCreateCard} 
            className="btn-create-new"
            disabled={!canCreateCard()}
            title={!canCreateCard() ? `Maximum limit of ${maxCardsLimit} cards reached` : 'Create a new business card'}
            style={{ opacity: !canCreateCard() ? 0.5 : 1, cursor: !canCreateCard() ? 'not-allowed' : 'pointer' }}
          >
            <Plus size={20} />
            <span>Create New Card</span>
          </button>
          <button 
            onClick={handleScanMyCard} 
            className="btn-create-new btn-scan-card"
            disabled={!canCreateCard()}
            title={!canCreateCard() ? `Maximum limit of ${maxCardsLimit} cards reached` : 'Scan your physical card to create a digital one'}
            style={{ opacity: !canCreateCard() ? 0.5 : 1, cursor: !canCreateCard() ? 'not-allowed' : 'pointer' }}
          >
            <Camera size={20} />
            <span>Scan My Card</span>
          </button>
        </div>
      </div>

      {error && (
        <div className="error-banner">
          <AlertCircle size={20} />
          <span>{error}</span>
          <button onClick={loadData} className="btn-retry">
            <RefreshCw size={16} />
            Retry
          </button>
        </div>
      )}

      {cards.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">
            <QrCode size={80} />
          </div>
          <h2>No Business Cards Yet</h2>
          <p>Create your first digital business card to get started with QR code sharing</p>
          <button 
            onClick={handleCreateCard} 
            className="btn-create-first"
            disabled={!canCreateCard()}
            style={{ opacity: !canCreateCard() ? 0.5 : 1, cursor: !canCreateCard() ? 'not-allowed' : 'pointer' }}
          >
            <Plus size={20} />
            Create Your First Card
          </button>
        </div>
      ) : (
        <div className="cards-grid">
          {cards.map((card) => (
            <BusinessCardItem
              key={card.cardId}
              card={card}
              onEdit={handleEditCard}
              onDelete={handleDeleteCard}
              onSetDefault={handleSetDefaultCard}
              onQrClick={handleQrClick}
              onViewCard={handleViewCard}
              showActions={true}
            />
          ))}
        </div>
      )}

      {/* QR Modal */}
      <QRModal
        isOpen={showQrModal}
        qrCodeUrl={selectedCard?.qrCodeUrl || ''}
        title={selectedCard?.cardName || ''}
        description={`${selectedCard?.displayName}'s Business Card`}
        onClose={() => {
          setShowQrModal(false);
          setSelectedCard(null);
        }}
        onDownload={handleDownloadQR}
        onDownloadVCard={handleDownloadVCard}
        onShare={handleShare}
        onEmail={handleEmailQR}
      />

      {/* Scan My Card Scanner */}
      <BusinessCardScanner
        isOpen={showScanner}
        onClose={() => setShowScanner(false)}
        onScanSuccess={handleScanSuccess}
        getAccessToken={() => getAccessToken(true)}
        mode="card"
      />
    </div>
  );
};

export default CardsList;
